import type { Calendar } from "@/components/calendars/api.ts";
import { getCalendars } from "@/components/calendars/api.ts";
import { useIsWide } from "@/hooks/use-mobile.tsx";
import { cn } from "@/lib/utils.ts";
import type { CalendarEventParticipant } from "@/types/events.ts";
import { FileText } from "lucide-react";
import type {
  DateSelectArg,
  DatesSetArg,
  EventApi,
  EventContentArg,
  EventDropArg,
  EventInput,
} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useQuery } from "@tanstack/react-query";
import { addDays, addMonths, addWeeks, format, subDays } from "date-fns";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CalendarFadeScrollbar } from "./CalendarFadeScrollbar.tsx";
import { useCalendarColors, useRegisteredCalendarColors } from "./calendarColors.ts";
import { reminderChipTime, reminderChipTitle } from "@/shared/reminderChip.ts";
import { isReminderBlob } from "./canEditEvent.ts";
import type { OkthxSourceBlob } from "@/types/calendarEvent.ts";
import {
  VIEW_TYPE_TO_FULLCALENDAR,
  VIEW_TYPE_TO_FULLCALENDAR_MOBILE,
  type ViewType,
} from "./types.ts";
import { sourceIdOf, stampHighlightedEvent } from "./eventTargeting.ts";
import { iterateMonthGridCells, normalizeSpan } from "./time-range-selection.ts";
import { useCalendarEvents } from "./useCalendarEvents.ts";
import "./calendar.css";

interface CalendarViewProps {
  viewType: ViewType;
  currentDate: Date;
  onDateChange: (date: Date) => void;
  hiddenCalendarIds?: Set<string>;
  onEventClick?: (eventId: string, anchorEl: HTMLElement) => void;
  /** Specific calendar IDs to fetch events for. If undefined, uses user preferences. Pass null to defer until preferences are loaded. */
  calendarIds?: string[] | null;
  /** Increment to trigger a scroll to the current time (e.g. when Today is clicked). */
  scrollToNowKey?: number;
  /**
   * Scroll the day/week grid to this time on mount (and when the value changes),
   * as "HH:mm" (24h). Ignored in month view. Set when a calendar focus link
   * carries a time range, so the grid lands at the right time-of-day.
   */
  initialScrollTime?: string;
  /**
   * Bump to re-scroll to the *same* `initialScrollTime`. Without it, re-applying
   * a focus link the user has since scrolled away from would be a no-op, since
   * the time itself never changed.
   */
  scrollToTimeKey?: number;
  /** Enable drag-to-select on the calendar. Requires @fullcalendar/interaction. */
  selectable?: boolean;
  /** Called when a time range is selected via drag. */
  onSelect?: (info: DateSelectArg) => void;
  /**
   * Fires on the first `selectAllow` of a drag — the cell where the user pressed.
   * Lets consumers distinguish drag-started-in-a-range (resize) from drag-
   * started-on-empty (create), which the normalized `onSelect` start/end
   * cannot reveal on its own.
   */
  onSelectStart?: (span: { start: Date; end: Date }) => void;
  /** Additional events to render (e.g. selected availability background events). */
  additionalEvents?: EventInput[];
  /** Custom event click handler that receives the raw FullCalendar event. */
  onRawEventClick?: (info: {
    event: { id: string; extendedProps: Record<string, unknown> };
    el?: HTMLElement;
    jsEvent?: Event;
  }) => void;
  /**
   * Single-tap handler for empty cells (no bg-event overlay). FullCalendar routes
   * taps on cells to `dateClick` and taps on events to `eventClick`, so this fires
   * only when tapping an unselected cell — pair with `onRawEventClick` for fully
   * symmetric tap-to-toggle.
   */
  onDateClick?: (info: { date: Date; allDay: boolean }) => void;
  /** Override the slot duration (default "01:00:00"). Use "00:30:00" for 30-min slots. */
  slotDuration?: string;
  /**
   * Granularity that clicks, drag-selections and event drags snap to. Defaults
   * to `slotDuration`. FullCalendar subdivides each slot into snaps for
   * hit-testing, so "00:15:00" lets a gesture land on :15 and :45 inside a
   * 30-minute row without changing row heights or labels.
   */
  snapDuration?: string;
  /**
   * Fires when an editable event has been resized via its native FC handle.
   * For per-event resize to work, set `editable: true` and (optionally)
   * `durationEditable` / `startEditable` on the EventInput. To get a visible
   * top-edge handle, pair with `eventResizableFromStart={true}` below.
   */
  onEventResize?: (info: {
    event: { id: string; start: Date; end: Date };
    revert: () => void;
  }) => void;
  /**
   * Show the native start-handle on editable events so the user can drag the
   * top edge. FC's calendar-level setting; the per-event `startEditable` flag
   * controls whether dragging it does anything. Defaults to false to match
   * the rest of the app.
   */
  eventResizableFromStart?: boolean;
  /**
   * Drag-to-reschedule wiring. When provided, these FC callbacks are merged
   * into the shared calendar config (overriding any defaults). The wrapping
   * `EditableCalendarView` constructs these from `useEventDrag`; bare
   * `CalendarView` callers (previews, availability picker) leave this unset
   * to keep events read-only.
   */
  eventDragFcProps?: {
    editable: boolean;
    eventStartEditable: boolean;
    eventDurationEditable: boolean;
    eventAllow: (
      dropInfo: { start: Date; end: Date; allDay: boolean },
      draggedEvent: EventApi | null,
    ) => boolean;
    eventDragStart: (info: { el: HTMLElement; event: EventApi }) => void;
    eventDragStop: (info: { el: HTMLElement; event: EventApi }) => void;
    eventDrop: (info: EventDropArg) => void;
  };
  /**
   * Commit a resized selection range. Fires on pointerup at the end of an
   * iOS-style handle drag. Mobile-only; the handles are only injected when
   * the carousel branch is active.
   */
  onResizeRange?: (id: string, newStart: Date, newEnd: Date) => void;
  /**
   * Fires when an editable event is moved (drag) or resized. Only events
   * carrying `editable: true` in `additionalEvents` are draggable; the
   * proposed-slots picker turns this on for fixed-duration slot edits. This
   * wires the parent's callback to FullCalendar's `eventDrop`/`eventResize`
   * (it does not, on its own, disable other interaction). `revert` undoes the
   * visual move — the parent must call it to reject a drop, since FullCalendar
   * keeps the event at the dropped position and does not reconcile back to
   * props mid-drag.
   */
  onEventChange?: (info: {
    event: { id: string; start: Date | null; end: Date | null };
    revert: () => void;
  }) => void;
  /**
   * Suppress the default click-to-open-Google-Calendar behavior for real
   * events. By default, clicking an event with no `onEventClick` opens its
   * `htmlLink`. Pickers that render real events only as read-only context
   * (e.g. the availability / proposed-slots editor) set this so those events
   * aren't interactive.
   */
  disableEventLinks?: boolean;
  /**
   * Ring a single event chip to draw attention to it (e.g. when the agent
   * references a source event and we focus it in the grid). Matched against each
   * event's server-resolved `instanceKey`.
   */
  highlightedInstanceKey?: string;
}

/** Get the current user's response status from event participants. */
const getSelfResponseStatus = (participants?: CalendarEventParticipant[]): string | undefined =>
  participants?.find((p) => p.self)?.state;

/**
 * How a chip carries its fill and its text colour, which depends on what is
 * drawing its edge.
 *
 * A briefed chip sets only its colour, and takes the brand purple rather than
 * its calendar's hue — the chip has stopped speaking for its calendar and
 * started speaking for us. It pairs with `--brief-wash` the way the task
 * badges pair a background with a foreground, and sits a step deeper than the
 * `--primary` the Today badge wears — that one has too little headroom over
 * white to survive a wash behind 12px text. Its fill and ring are two background
 * layers composed by `.cal-event-brief`, so it sets no background here: an
 * inline one would replace both and take the ring with it. Everything inside
 * inherits the colour, including the time, which `.cal-event-block-time` fades
 * to 70% rather than recolouring.
 *
 * Guard clauses rather than one expression: the three cases are different
 * things, not variations on a calculation.
 */
function chipSurface({
  color,
  tintPct,
  isOutlineStyle,
  hasBrief,
}: {
  /** The calendar's colour, which a filled chip is both tinted and lettered from. */
  color: string;
  /** How much of it the fill carries. All-day chips sit heavier than timed ones. */
  tintPct: number;
  isOutlineStyle: boolean;
  hasBrief: boolean;
}): React.CSSProperties {
  // Tentative and declined chips drop their fill entirely and keep the plain
  // ring `.cal-event-outline` draws, which wins over the brief's gradient. With
  // no tint behind it the text takes the calendar colour undarkened.
  if (isOutlineStyle) return { background: "var(--card)", color };
  if (hasBrief) return { color: "var(--brief-foreground)" };
  return {
    background: `color-mix(in srgb, ${color} ${tintPct}%, var(--card))`,
    // Far enough toward black to read against the tint it sits on.
    color: `color-mix(in srgb, ${color} 60%, black)`,
  };
}

/**
 * Marks a meeting Okthx has written a brief about — there is something here to
 * read, and clicking through gets to it.
 *
 * Inline inside the title rather than a flex sibling beside it: chips render in
 * two layouts, a row (`cal-event`) and a column (`cal-event-block`), and a
 * sibling that sits left of the title in one stacks above it in the other. As
 * an inline lead-in it lands in the same place in both, and the title's own
 * `text-overflow: ellipsis` still trims from the end.
 *
 * Stroked, not filled. The detail inside a document glyph is drawn as strokes
 * over an unfilled outline, so painting it solid collapses the whole thing into
 * a rectangle. At this size the 2px viewBox stroke scales down to well under a
 * pixel, which lands close to the weight of the text beside it.
 *
 * Takes whatever colour the chip is already lettered in, through `currentColor`.
 * On a ringed chip that is `--brief-foreground`, so the mark reads as part of
 * one purple treatment. The month row and the list draw no ring, and their text
 * is the ordinary foreground — there the mark is a glyph beside a title rather
 * than part of a coloured surface, and the calendar's own hue stays in the dot.
 *
 * Size and offset are both measured against the glyph's *ink* rather than its
 * box, since a lucide icon does not fill its viewBox. A page outline spans
 * about five sixths of it, so `0.9em` puts the ink at roughly 9px against a cap
 * of 8.73px and the nudge sits its centre on the cap band. Both are in `em`, so
 * they hold if the chip's type scale moves.
 */
const BriefMark = () => (
  <FileText
    role="img"
    aria-label="Has a brief"
    className="mr-1 inline size-[0.9em] align-[-0.096em]"
  />
);

/**
 * Custom event content renderer for FullCalendar.
 * Renders differently based on the view type.
 */
const EventContent = ({
  eventInfo,
  isListView,
  isTimeGrid,
}: {
  eventInfo: EventContentArg;
  isListView: boolean;
  isTimeGrid: boolean;
}) => {
  const { calendarId, colorId, participants, pendingSyncUpdate, isPast, okthx } =
    eventInfo.event.extendedProps;
  // `extendedProps` is untyped, and this one reaches JSX as
  // `{hasBrief && <BriefMark />}`, where a falsy value React can render — 0,
  // "" — prints itself into the chip's title instead of rendering nothing.
  // Narrowing once here makes every use safe by construction.
  const hasBrief = !!eventInfo.event.extendedProps["briefId"];
  // Subscribing here is what repaints a chip when its calendar's colour changes:
  // FullCalendar keeps these mounted, so nothing above re-renders them.
  const { getEventHex } = useCalendarColors();

  // Skip rendering for events without a calendarId (e.g. background availability
  // selections, FullCalendar selectMirror elements during drag)
  if (!calendarId) return null;

  const isReminder = isReminderBlob(okthx as OkthxSourceBlob | undefined);

  const color = getEventHex(calendarId as string, colorId as string | undefined);
  const selfStatus = getSelfResponseStatus(participants as CalendarEventParticipant[] | undefined);
  const isTentative = !!selfStatus && selfStatus !== "attending" && selfStatus !== "declined";
  const isDeclined = selfStatus === "declined";
  const isOutlineStyle = isTentative || isDeclined;
  const isPendingSync = !!pendingSyncUpdate;

  // No onClick: FullCalendar's delegated eventClick listener on .fc-event handles
  // all event clicks uniformly on desktop and mobile. Adding onClick here would
  // double-fire — React 17+ attaches its delegated root listener above the .fc
  // container, so FC's native listener fires first on bubble and stopPropagation
  // inside the button runs too late to suppress it.
  if (isListView) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 w-full text-left py-1",
          isPendingSync && "opacity-40 pointer-events-none",
          isPast && !isPendingSync && "opacity-60",
        )}
      >
        <div className="size-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
        <span
          className={cn("truncate min-w-0", isDeclined && "cal-event-declined")}
          style={isOutlineStyle ? { color } : undefined}
        >
          <span className="cal-event-title">
            {hasBrief && <BriefMark />}
            {eventInfo.event.title}
          </span>
        </span>
      </div>
    );
  }

  // A reminder is a moment, not a span, so its chip carries one time. The
  // generic branches below print start–end, which on a reminder would read as
  // the half-hour the grid draws it over rather than when it actually fires.
  if (isTimeGrid && isReminder && eventInfo.event.start) {
    return (
      <div
        className={cn(
          "cal-event-block cal-event-reminder",
          isPendingSync && "opacity-40 pointer-events-none",
          isPast && !isPendingSync && "opacity-60",
        )}
        style={
          {
            "--event-color": color,
            color: `color-mix(in srgb, ${color} 60%, black)`,
            background: `color-mix(in srgb, ${color} 15%, var(--card))`,
          } as React.CSSProperties
        }
      >
        <span className="cal-event-block-title">
          {reminderChipTitle(eventInfo.event.title)}
          {`, ${reminderChipTime(eventInfo.event.start)}`}
        </span>
      </div>
    );
  }

  if (isTimeGrid && !eventInfo.event.allDay) {
    const start = eventInfo.event.start;
    const end = eventInfo.event.end;
    const durationMin = start && end ? (end.getTime() - start.getTime()) / 60000 : 60;
    const isShort = durationMin < 60;

    let timeStr = "";
    if (isShort && start) {
      const h = start.getHours();
      const m = start.getMinutes();
      const meridiem = h >= 12 ? "pm" : "am";
      const hour = h % 12 || 12;
      timeStr =
        m > 0 ? `${hour}:${m.toString().padStart(2, "0")}${meridiem}` : `${hour}${meridiem}`;
    }

    return (
      <div
        className={cn(
          "cal-event-block",
          hasBrief && "cal-event-brief",
          isOutlineStyle && "cal-event-outline",
          isDeclined && "cal-event-declined",
          isPendingSync && "opacity-40 pointer-events-none",
          isPast && !isPendingSync && "opacity-60",
        )}
        style={
          {
            "--event-color": color,
            ...chipSurface({ color, tintPct: 15, isOutlineStyle, hasBrief }),
          } as React.CSSProperties
        }
      >
        <span className="cal-event-block-title">
          {hasBrief && <BriefMark />}
          {eventInfo.event.title}
          {timeStr && `, ${timeStr}`}
        </span>
        {!isShort && eventInfo.timeText && (
          <span className="cal-event-block-time">{eventInfo.timeText}</span>
        )}
      </div>
    );
  }

  if (eventInfo.event.allDay) {
    return (
      <div
        className={cn(
          "cal-event cal-event-allday",
          hasBrief && "cal-event-brief",
          isOutlineStyle && "cal-event-outline",
          isDeclined && "cal-event-declined",
          isPendingSync && "opacity-40 pointer-events-none",
          isPast && !isPendingSync && "opacity-60",
        )}
        style={
          {
            "--event-color": color,
            ...chipSurface({ color, tintPct: 25, isOutlineStyle, hasBrief }),
          } as React.CSSProperties
        }
      >
        <span className="cal-event-title">
          {hasBrief && <BriefMark />}
          {eventInfo.event.title}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "cal-event cal-event-timed",
        isDeclined && "cal-event-declined",
        isPendingSync && "opacity-40 pointer-events-none",
      )}
      style={{ color: isOutlineStyle ? color : "var(--foreground)" }}
    >
      <span className="cal-event-dot" style={{ backgroundColor: color }} />
      {isReminder && eventInfo.event.start ? (
        <span className="cal-event-time">{reminderChipTime(eventInfo.event.start)}</span>
      ) : (
        eventInfo.timeText && <span className="cal-event-time">{eventInfo.timeText}</span>
      )}
      <span className="cal-event-title">
        {hasBrief && <BriefMark />}
        {isReminder ? reminderChipTitle(eventInfo.event.title) : eventInfo.event.title}
      </span>
    </div>
  );
};

/**
 * Week/day column header: "Sun 12", with today's date number wrapped in a
 * primary rounded square (weekday text stays foreground-colored beside it —
 * the badge, not the text, is the "today" signal). `ml-0.5` gives the badge
 * 2px of breathing room after the weekday so it doesn't crowd.
 *
 * Every date number gets the same size-5 box — transparent on ordinary days,
 * painted on today — so the header row's height (and the grid top edge below
 * it) is identical whether or not the visible range contains today.
 *
 * HTML injection is safe — `weekday` and `day` are derived from a Date via
 * `date-fns format`, both fixed-format strings (no user input).
 */
const renderWeekDayHeader = (arg: { date: Date; isToday: boolean }) => {
  const weekday = format(arg.date, "EEE");
  const day = format(arg.date, "d");
  // Ordinary days inherit the cushion's muted color; only today darkens the
  // weekday and fills the number badge.
  const weekdayClass = arg.isToday ? "text-foreground" : "";
  const badge = arg.isToday ? "bg-primary text-primary-foreground font-semibold" : "font-medium";
  return {
    html: `<span class="${weekdayClass}">${weekday}</span> <span class="ml-0.5 size-5 inline-flex items-center justify-center rounded text-xs leading-none ${badge}">${day}</span>`,
  };
};

/** Shared FullCalendar view config for carousel slots — supports every view
 * the mobile/tablet picker exposes (single-day, multi-day, week, month) so
 * the same three FC instances can `changeView()` between them without
 * needing different plugin configs per slot.
 *
 * `timeGrid2Day` is the iOS-style two-day-side-by-side grid that powers the
 * "multi-day" ViewType. Its column headers match week view ("Sat 26", today
 * badged) so each column says which day it is, as native's 2-day view does.
 *
 * Mobile timegrid views cap `dayMaxEvents` at 5 instead of inheriting the
 * shared `dayMaxEvents: true`. With `true`, FullCalendar measures the all-day
 * cell's height and clamps to a single visible event on the narrower mobile
 * viewport; with an explicit count, up to 5 events show before a "+more" link
 * — comfortably covers the common 2–3-event case, bounded so a day with many
 * holidays or multi-day spans can't grow the all-day row until it dominates
 * the viewport. Month view keeps the inherited `dayMaxEvents: true` —
 * height-based capping is the right behavior there. */
const CAROUSEL_VIEW_CONFIG = {
  timeGrid2Day: {
    type: "timeGrid",
    duration: { days: 2 },
    dayHeaderContent: renderWeekDayHeader,
    slotDuration: "00:30:00",
    dayMaxEvents: 5,
  },
  timeGridDay: {
    dayHeaderContent: renderWeekDayHeader,
    slotDuration: "00:30:00",
    dayMaxEvents: 5,
  },
  timeGridWeek: {
    dayHeaderContent: renderWeekDayHeader,
    slotDuration: "00:30:00",
    dayMaxEvents: 5,
  },
  dayGridMonth: {},
};

/** Returns the scroll container inside a slot div, or null. */
function getSlotScroller(slotEl: HTMLElement | null): Element | null {
  return slotEl?.querySelector(".fc-scroller-liquid-absolute") ?? null;
}

/** Format a Date as "YYYY-MM-DD" to match FullCalendar's data-date attribute. */
function toDateAttr(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

/** Snap a minute count to the nearest 30-min slot and clamp to [0, span]. */
function snapMinutes(minutes: number, span: number): number {
  const clamped = Math.max(0, Math.min(span, minutes));
  return Math.round(clamped / 30) * 30;
}

/**
 * Compute the per-column overlay boxes for a drag span (box selection).
 * Returns one entry per day column with percentage-based top/bottom for positioning
 * inside FullCalendar's `.fc-timegrid-cols` container.
 *
 * `slotSpanMinutes` is the total minute span rendered by the grid (slotMaxTime −
 * slotMinTime). Must match the container's actual height — otherwise percentage
 * positioning drifts from the grid. Mobile carousel uses 1470 (24:30:00 slotMaxTime),
 * non-carousel uses FullCalendar's default 1440.
 */
function computeDragBoxes(
  rawStart: Date,
  rawEnd: Date,
  slotSpanMinutes: number,
): { dateStr: string; topPct: number; bottomPct: number }[] {
  const [start, end] = normalizeSpan(rawStart, rawEnd);

  const startTOD = start.getHours() * 60 + start.getMinutes();
  const endTOD = end.getHours() * 60 + end.getMinutes();
  const sameDay = start.toDateString() === end.toDateString();

  // Midnight (00:00) on a different day means "end of previous day" — treat as 1440 minutes.
  // Mirrors decomposeBoxSelection's midnight handling in time-range-selection.ts.
  const effectiveEndTOD = endTOD === 0 && !sameDay ? 1440 : endTOD;
  const topMin = Math.min(startTOD, effectiveEndTOD);
  const botMin = Math.max(startTOD, effectiveEndTOD);
  const topPct = (topMin / slotSpanMinutes) * 100;
  const bottomPct = (botMin / slotSpanMinutes) * 100;

  const firstDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const rawLastDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const lastDay =
    endTOD === 0 && !sameDay
      ? new Date(rawLastDay.getFullYear(), rawLastDay.getMonth(), rawLastDay.getDate() - 1)
      : rawLastDay;

  const boxes: { dateStr: string; topPct: number; bottomPct: number }[] = [];
  const d = new Date(firstDay);
  while (d <= lastDay) {
    boxes.push({ dateStr: toDateAttr(d), topPct, bottomPct });
    d.setDate(d.getDate() + 1);
  }
  return boxes;
}

export const CalendarView = ({
  viewType,
  currentDate,
  onDateChange,
  hiddenCalendarIds,
  onEventClick,
  calendarIds,
  scrollToNowKey,
  initialScrollTime,
  scrollToTimeKey,
  selectable,
  onSelect,
  onSelectStart,
  additionalEvents,
  onRawEventClick,
  onDateClick,
  slotDuration: slotDurationProp,
  snapDuration,
  onEventResize,
  eventResizableFromStart,
  eventDragFcProps,
  onResizeRange,
  onEventChange,
  disableEventLinks,
  highlightedInstanceKey,
}: CalendarViewProps) => {
  const isWide = useIsWide();

  // --- Custom drag box overlay (replaces FullCalendar's built-in mirror) ---
  // We inject absolutely-positioned divs into each day column's background container
  // during drag, giving us pixel-perfect rectangular box selection across columns.
  const dragOverlayEls = useRef<HTMLElement[]>([]);
  // True while a FullCalendar selection is in progress (mouse drag or touch long-press).
  // Prevents carousel swipe/scroll while the user is dragging a time range.
  const isSelectingRef = useRef(false);
  // True once the carousel has claimed the current touch gesture as a horizontal
  // swipe. Latched until the next touchstart. While set, `selectAllow` returns
  // false so FC cannot start (or re-start) a selection mid-swipe — without this,
  // a long-press that armed FC before the user committed to a pan would keep
  // re-firing selectAllow on every move and the swipe would be hijacked.
  const swipeWonRef = useRef(false);
  // Tap-hold preview timer: fires at longPressDelay and renders a pressed-state
  // overlay for the cell under the finger. Paired with `selectMinDistance: 5`,
  // which prevents FC's own `selectAllow` from firing on tap-hold (FC requires
  // 5px of movement before treating a gesture as a selection, so without this
  // manual preview the user has no visual feedback during the hold).
  const holdTimerRef = useRef<number | null>(null);
  // Whether the current press held long enough for the tap-hold preview to fire.
  // On touch, a single-cell selection (`dateClick`) only commits if this is true
  // — so a quick tap on an empty cell does nothing; you must tap-and-hold. Reset
  // at each press start; set when the preview timer fires.
  const holdPreviewFiredRef = useRef(false);
  // Pointer type of the current press, so `dateClick` can keep mouse clicks
  // immediate while gating touch taps behind the hold above.
  const pressPointerTypeRef = useRef<string>("mouse");
  // Refs for touch-handler callbacks (which keep empty deps — see handleTouchStart).
  // Kept in sync with props/memoized callbacks via the ref-sync effect below.
  const selectableRef = useRef(selectable);
  selectableRef.current = selectable;
  const onEventResizeRef = useRef(onEventResize);
  onEventResizeRef.current = onEventResize;
  const onSelectStartRef = useRef(onSelectStart);
  onSelectStartRef.current = onSelectStart;
  const clearDragOverlay = useCallback(() => {
    for (const el of dragOverlayEls.current) el.remove();
    dragOverlayEls.current = [];
  }, []);

  // --- Month view rectangular drag highlight ---
  // Instead of injecting overlay divs (like timegrid), we toggle a CSS class on day cells
  // to highlight the rectangular selection (day-of-week columns × week rows).
  const MONTH_HIGHLIGHT_CLASS = "month-drag-highlight";
  const clearMonthHighlight = useCallback(() => {
    for (const el of document.querySelectorAll(`.${MONTH_HIGHLIGHT_CLASS}`)) {
      el.classList.remove(MONTH_HIGHLIGHT_CLASS);
    }
  }, []);

  /**
   * Highlight rectangular grid cells in month view during drag.
   * Month-view equivalent of renderDragOverlay (which handles timegrid).
   * Uses the shared iterateMonthGridCells utility to compute the day-of-week
   * columns × week rows rectangle, then toggles a CSS class on each cell.
   *
   * IMPORTANT: carousel mode keeps three month instances in the DOM (prev /
   * current / next). Adjacent months share boundary weeks because the grid
   * pads to full weeks, so a given `data-date` often matches in MULTIPLE
   * slots' DOM. `document.querySelector` returns the first match in DOM
   * order (slot 0 — the offscreen-left prev month), which is why unscoped
   * lookups applied the highlight to an invisible cell. Always scope the
   * lookup to a specific FC root.
   */
  const highlightMonthCells = useCallback(
    (rawStart: Date, rawEnd: Date, rootEl?: HTMLElement | null) => {
      clearMonthHighlight();
      const scope: ParentNode = rootEl ?? document;
      const [s, e] = normalizeSpan(rawStart, rawEnd);
      iterateMonthGridCells(s, e, (day) => {
        const cell = scope.querySelector(`.fc-daygrid-day[data-date="${toDateAttr(day)}"]`);
        cell?.classList.add(MONTH_HIGHLIGHT_CLASS);
      });
    },
    [clearMonthHighlight],
  );

  /** Render timegrid drag overlay boxes inside `.fc-timegrid-cols` using column bounding rects. */
  const renderDragOverlay = useCallback(
    (boxes: { dateStr: string; topPct: number; bottomPct: number }[]) => {
      if (boxes.length === 0) return;
      try {
        const firstCol = document.querySelector(
          `.fc-timegrid-col[data-date="${boxes[0].dateStr}"]`,
        );
        const lastCol = document.querySelector(
          `.fc-timegrid-col[data-date="${boxes[boxes.length - 1].dateStr}"]`,
        );
        const container = firstCol?.closest(".fc-timegrid-cols") as HTMLElement | null;
        if (firstCol && lastCol && container) {
          const cRect = container.getBoundingClientRect();
          const fRect = firstCol.getBoundingClientRect();
          const lRect = lastCol.getBoundingClientRect();
          const el = document.createElement("div");
          el.className = "drag-box-overlay";
          el.style.position = "absolute";
          el.style.left = `${fRect.left - cRect.left}px`;
          el.style.width = `${lRect.right - fRect.left}px`;
          el.style.top = `${boxes[0].topPct}%`;
          el.style.bottom = `${100 - boxes[0].bottomPct}%`;
          container.appendChild(el);
          dragOverlayEls.current.push(el);
        }
      } catch (e) {
        console.warn("Drag overlay render skipped:", e);
      }
    },
    [],
  );
  // Safety cleanup: cancel pending preview timer on any pointer-up. We intentionally
  // DO NOT clear the overlay here — that's now handled by the `additionalEvents`
  // layout effect below, which fires after the committed bg-event has rendered,
  // avoiding a visual flash between preview removal and bg-event appearance.
  useEffect(() => {
    const onUp = () => {
      if (holdTimerRef.current !== null) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      isSelectingRef.current = false;
    };
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchend", onUp);
    return () => {
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchend", onUp);
      if (holdTimerRef.current !== null) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      clearDragOverlay();
      clearMonthHighlight();
    };
  }, [clearDragOverlay, clearMonthHighlight]);

  // Clear the press-preview in lockstep with the committed bg-event rendering.
  // useLayoutEffect fires after React commits (so FullCalendar has reconciled the
  // new `additionalEvents` and its bg-event is in the DOM) but BEFORE browser paint,
  // so the overlay removal and bg-event appearance happen in the same paint.
  //
  // `additionalEvents` MUST be in the deps — it's the signal that state committed.
  // The two callback deps are both stable (`useCallback([])`) so without the prop,
  // this effect only fires on mount and every preview rendered thereafter becomes
  // a permanent remnant (observed: tapping to deselect a cell leaves its preview
  // highlighted forever, on all views — day / week / month, desktop and mobile).
  //
  // Previously we cleared via `requestAnimationFrame` in the pointerup handler,
  // which raced with FC's internal layout (`updateSize` via its own rAF) in the
  // timegrid path — the overlay was removed one frame before the bg-event painted,
  // producing a "preview → blank → committed" flash on desktop mouse (mouse uses
  // 0ms preview delay, so every click hit this path). Month view didn't flash
  // because daygrid commits in the same frame, so the rAF timing happened to line up.
  // Driving the clear from state commit rather than pointer release removes the race.
  useLayoutEffect(() => {
    // Read `additionalEvents` so biome doesn't strip it from the dep array — we
    // only care about the identity change as a signal, not the value itself.
    void additionalEvents;
    clearDragOverlay();
    clearMonthHighlight();
  }, [additionalEvents, clearDragOverlay, clearMonthHighlight]);

  /**
   * Render a pressed-state preview on the cell at the tap-hold's location —
   * the mate to FC's `selectAllow` visual, used when `selectMinDistance: 5`
   * suppresses FC's own selection on tap-hold-no-drag. Fired from the
   * touchstart long-press timer so the user sees feedback during the hold.
   * The overlay is auto-cleared on touchend by the safety-cleanup effect.
   *
   * We hit-test by (clientX, clientY) instead of `target.closest(...)` because
   * both layouts use dual DOM subtrees:
   *  - Timegrid: touch lands on `.fc-timegrid-slot-lane` inside `.fc-timegrid-slots`,
   *    which is a SEPARATE subtree from `.fc-timegrid-cols` — so `closest` never
   *    finds the day column. Week view has 7 columns; picking the first (via
   *    `querySelector`) always rendered on Sunday/Monday regardless of tap.
   *  - Daygrid (month): touch can land inside `.fc-daygrid-bg-harness` (the
   *    background cells), which isn't an ancestor of the `.fc-daygrid-day`
   *    cell that carries `data-date`. `closest` returns null.
   * Walking up to the FC view root + matching against rect bounds works for
   * both — unambiguously picks the column/cell actually under the finger.
   */
  const renderTapHoldPreview = useCallback(
    (target: EventTarget | null, clientX: number, clientY: number) => {
      if (!(target instanceof Element)) return;
      const fcRoot = target.closest<HTMLElement>(".fc-view-harness, .fc-view");
      if (!fcRoot) return;

      // Branch on view type by DOM signature. IMPORTANT: timegrid views also
      // render an all-day daygrid row at the top, so the presence of
      // `.fc-daygrid-day` cells does NOT imply month view. `.fc-timegrid-col`
      // only appears in timegrid views — use it as the reliable discriminator.
      const timegridCols = fcRoot.querySelectorAll<HTMLElement>(
        ".fc-timegrid-col[data-date] .fc-timegrid-col-frame",
      );

      if (timegridCols.length === 0) {
        // Month view (pure daygrid): find day cell whose rect contains the touch.
        const dayCells = fcRoot.querySelectorAll<HTMLElement>(".fc-daygrid-day[data-date]");
        for (const cell of dayCells) {
          const r = cell.getBoundingClientRect();
          if (clientX >= r.left && clientX < r.right && clientY >= r.top && clientY < r.bottom) {
            const dayStr = cell.getAttribute("data-date");
            if (!dayStr) continue;
            const [y, m, d] = dayStr.split("-").map(Number);
            const day = new Date(y, m - 1, d);
            // `iterateMonthGridCells` treats `end` as exclusive (FC convention),
            // so pass next-day midnight to highlight just this single cell.
            const nextDay = new Date(y, m - 1, d + 1);
            highlightMonthCells(day, nextDay, fcRoot);
            return;
          }
        }
        return;
      }

      // Timegrid (day/week): find the day column whose rect contains clientX.
      // Day view has one column — matches trivially. Week view has 7; previous
      // version used `querySelector` (first match) and always landed on the
      // leftmost column. (Note: taps on the all-day row fall through without
      // matching any col on X *and* Y; for now we simply skip the preview there.)
      let frameEl: HTMLElement | null = null;
      for (const col of timegridCols) {
        const r = col.getBoundingClientRect();
        if (clientX >= r.left && clientX < r.right) {
          frameEl = col;
          break;
        }
      }
      if (!frameEl) return;

      const frameRect = frameEl.getBoundingClientRect();
      const yInFrame = clientY - frameRect.top;
      const slotSpanMinutes = isCarouselRef.current ? 1470 : 1440;
      const totalMin = (yInFrame / frameRect.height) * slotSpanMinutes;
      const startMin = Math.max(0, Math.floor(totalMin / 30) * 30);
      const topPct = (startMin / slotSpanMinutes) * 100;
      const bottomPct = ((startMin + 30) / slotSpanMinutes) * 100;

      const el = document.createElement("div");
      el.className = "drag-box-overlay";
      el.style.position = "absolute";
      el.style.left = "0";
      el.style.right = "0";
      el.style.top = `${topPct}%`;
      el.style.bottom = `${100 - bottomPct}%`;
      frameEl.appendChild(el);
      dragOverlayEls.current.push(el);
    },
    [highlightMonthCells],
  );
  // Ref-synced version so `handleTouchStart` can call the latest closure
  // without taking it as a dependency (keeps empty deps — see that callback).
  const renderTapHoldPreviewRef = useRef(renderTapHoldPreview);
  renderTapHoldPreviewRef.current = renderTapHoldPreview;

  // --- iOS-style resize handles on time-range selection bg-events ---
  // Mobile-only. When the user drags either handle we hide the source
  // bg-event (via .time-range-resizing) and render the existing
  // .drag-box-overlay as a live preview, committing on pointerup via
  // `onResizeRange`. `isResizingRef` is checked in the carousel touchmove
  // handler below so the horizontal swipe is suppressed while the handle
  // is being dragged.
  const isResizingRef = useRef(false);
  const onResizeRangeRef = useRef(onResizeRange);
  onResizeRangeRef.current = onResizeRange;

  const resizeStateRef = useRef<{
    rangeId: string;
    handle: "top" | "bottom";
    bgEventEl: HTMLElement;
    frameEl: HTMLElement;
    /** Local midnight of the column the range lives in. */
    columnDate: Date;
    /** Grid total minute span (1470 in carousel, 1440 otherwise). */
    slotSpanMinutes: number;
    /** Time of the OTHER end of the range — stays fixed during the drag. */
    anchorMs: number;
    /** Most-recent committed range, used to commit identical values on no-move taps. */
    lastStartMs: number;
    lastEndMs: number;
    /**
     * The exact handler references passed to addEventListener at gesture
     * start. Captured here so teardown can pass the same references to
     * removeEventListener — even if React Query (or anything else) triggers
     * a re-render mid-gesture that would otherwise hand teardown a fresh
     * callback identity and leak the original listener.
     */
    moveHandler: (e: PointerEvent) => void;
    upHandler: () => void;
  } | null>(null);

  /**
   * Recompute the live overlay box from the current resize state + a finger
   * Y position. Returns [startMs, endMs] in the column's local timezone.
   * Enforces a 30-minute minimum width by pushing the dragged edge back
   * against the anchor — symmetric for both top and bottom handles.
   */
  const computeResizeRange = useCallback((clientY: number): [number, number] | null => {
    const state = resizeStateRef.current;
    if (!state) return null;
    const { frameEl, columnDate, slotSpanMinutes, anchorMs, handle } = state;
    const frameRect = frameEl.getBoundingClientRect();
    const yInFrame = clientY - frameRect.top;
    const totalMin = (yInFrame / frameRect.height) * slotSpanMinutes;
    const snappedMin = snapMinutes(totalMin, slotSpanMinutes);
    const newTimeMs = columnDate.getTime() + snappedMin * 60_000;
    const MIN_MS = 30 * 60_000;
    if (handle === "top") {
      const newEnd = anchorMs;
      const newStart = Math.min(newTimeMs, newEnd - MIN_MS);
      return [newStart, newEnd];
    }
    const newStart = anchorMs;
    const newEnd = Math.max(newTimeMs, newStart + MIN_MS);
    return [newStart, newEnd];
  }, []);

  /**
   * Render the live drag-box overlay for an in-progress resize.
   *
   * Injects the overlay directly into the column frame captured at
   * `beginResize` time. We do NOT call `renderDragOverlay` here because it
   * looks the column up via `document.querySelector('[data-date=...]')`,
   * and in carousel mode three slots simultaneously contain the same
   * `data-date` (slot 0/1/2 overlap by one day in the 2-day view) —
   * `querySelector` returns the first DOM-order match (slot 0, offscreen)
   * and the overlay ends up invisible. Same trap as `highlightMonthCells`.
   */
  const paintResizeOverlay = useCallback(
    (startMs: number, endMs: number) => {
      const state = resizeStateRef.current;
      if (!state) return;
      const startD = new Date(startMs);
      const endD = new Date(endMs);
      const slotSpanMinutes = state.slotSpanMinutes;
      const startTOD = startD.getHours() * 60 + startD.getMinutes();
      let endTOD = endD.getHours() * 60 + endD.getMinutes();
      // End-at-midnight is next day's 00:00 — treat as the full slot span.
      if (endTOD === 0 && endMs > startMs) endTOD = slotSpanMinutes;
      const topPct = (startTOD / slotSpanMinutes) * 100;
      const bottomPct = (endTOD / slotSpanMinutes) * 100;

      clearDragOverlay();
      const el = document.createElement("div");
      el.className = "drag-box-overlay";
      el.style.position = "absolute";
      el.style.left = "0";
      el.style.right = "0";
      el.style.top = `${topPct}%`;
      el.style.bottom = `${100 - bottomPct}%`;
      state.frameEl.appendChild(el);
      dragOverlayEls.current.push(el);
    },
    [clearDragOverlay],
  );

  const handleResizePointerMove = useCallback(
    (e: PointerEvent) => {
      const range = computeResizeRange(e.clientY);
      if (!range) return;
      e.preventDefault();
      const [startMs, endMs] = range;
      const state = resizeStateRef.current;
      if (state) {
        state.lastStartMs = startMs;
        state.lastEndMs = endMs;
      }
      paintResizeOverlay(startMs, endMs);
    },
    [computeResizeRange, paintResizeOverlay],
  );

  const teardownResize = useCallback(() => {
    const state = resizeStateRef.current;
    if (state) {
      state.bgEventEl.classList.remove("time-range-resizing");
      // Remove the EXACT references that beginResize registered. Reading
      // from state guarantees identity match even if a re-render happened
      // mid-gesture and refreshed the underlying callbacks.
      document.removeEventListener("pointermove", state.moveHandler);
      document.removeEventListener("pointerup", state.upHandler);
      document.removeEventListener("pointercancel", state.upHandler);
    }
    clearDragOverlay();
    resizeStateRef.current = null;
    isResizingRef.current = false;
    isSelectingRef.current = false;
  }, [clearDragOverlay]);

  const handleResizePointerUp = useCallback(() => {
    const state = resizeStateRef.current;
    if (!state) {
      teardownResize();
      return;
    }
    onResizeRangeRef.current?.(
      state.rangeId,
      new Date(state.lastStartMs),
      new Date(state.lastEndMs),
    );
    teardownResize();
  }, [teardownResize]);

  // Latest-pointerup ref so beginResize can register the freshest handler at
  // gesture start. Teardown does NOT read this ref — it reads the exact
  // reference captured on resizeStateRef.upHandler so add/remove identities
  // always match, immune to any re-render that happens mid-gesture.
  const handleResizePointerUpRef = useRef(handleResizePointerUp);
  handleResizePointerUpRef.current = handleResizePointerUp;

  /**
   * Start a resize gesture from a handle pointerdown.
   * Captures the column geometry and the anchor (the OTHER end of the range,
   * which stays fixed during the drag).
   */
  const beginResize = useCallback(
    (
      e: PointerEvent,
      handle: "top" | "bottom",
      bgEventEl: HTMLElement,
      rangeId: string,
      originalStart: Date,
      originalEnd: Date,
    ) => {
      e.stopPropagation();
      e.preventDefault();
      const frameEl = bgEventEl.closest<HTMLElement>(".fc-timegrid-col-frame");
      const colEl = bgEventEl.closest<HTMLElement>(".fc-timegrid-col[data-date]");
      if (!frameEl || !colEl) return;
      const dateStr = colEl.dataset["date"];
      if (!dateStr) return;
      const [y, m, d] = dateStr.split("-").map(Number);
      const columnDate = new Date(y, m - 1, d);
      const slotSpanMinutes = isCarouselRef.current ? 1470 : 1440;
      const anchorMs = handle === "top" ? originalEnd.getTime() : originalStart.getTime();

      // Bind locally so the exact references go to BOTH addEventListener and
      // resizeStateRef. Teardown reads from state and removes the same refs.
      const moveHandler = handleResizePointerMove;
      const upHandler = handleResizePointerUpRef.current;
      resizeStateRef.current = {
        rangeId,
        handle,
        bgEventEl,
        frameEl,
        columnDate,
        slotSpanMinutes,
        anchorMs,
        lastStartMs: originalStart.getTime(),
        lastEndMs: originalEnd.getTime(),
        moveHandler,
        upHandler,
      };
      isResizingRef.current = true;
      // Reuse the existing carousel-swipe gate so we don't have to thread a
      // second flag through the touchmove handler.
      isSelectingRef.current = true;
      bgEventEl.classList.add("time-range-resizing");
      paintResizeOverlay(originalStart.getTime(), originalEnd.getTime());

      document.addEventListener("pointermove", moveHandler, { passive: false });
      document.addEventListener("pointerup", upHandler);
      document.addEventListener("pointercancel", upHandler);
    },
    [handleResizePointerMove, paintResizeOverlay],
  );
  const beginResizeRef = useRef(beginResize);
  beginResizeRef.current = beginResize;

  /**
   * FullCalendar `eventDidMount` hook — injects the two resize handles into
   * each selection bg-event when the carousel (mobile) branch is active.
   * Handles are plain DOM divs with their own pointerdown listeners; the
   * bg-event itself keeps `pointer-events: none` (so taps on the body still
   * fall through to the underlying slot for deselect), and the handles
   * opt back in via `pointer-events: auto` in CSS.
   *
   * Step 1 scope: single-day ranges only. A range that crosses midnight is
   * skipped — those are rare in our selection flow (decomposeBoxSelection
   * already splits multi-day drags per-day) and the handle UX needs
   * column-specific geometry that's harder for multi-column ranges.
   */
  // Per-range record of every handle pair we've injected. A range can
  // mount in multiple FC slots simultaneously (the carousel has 3 FC
  // instances and adjacent slots share dates), so each entry is a flat
  // list of all handle elements across all instances of that id.
  const injectedHandlesRef = useRef<Map<string, HTMLElement[]>>(new Map());

  const handleEventDidMount = useCallback(
    (arg: {
      event: {
        id: string;
        start: Date | null;
        end: Date | null;
        extendedProps: Record<string, unknown>;
      };
      el: HTMLElement;
    }) => {
      if (!arg.event.extendedProps["isTimeRangeSelection"]) return;
      if (!isCarouselRef.current) return;
      const { start, end, id } = arg.event;
      if (!start || !end) return;
      // Single-day check: use the day before end-of-range so 00:00 next-day
      // (exclusive end) still counts as same-day.
      const endInclusive = new Date(end.getTime() - 1);
      if (start.toDateString() !== endInclusive.toDateString()) return;

      // Wipe any stale handles on THIS specific element (FC recycles bg-event
      // DOM). Scoped to arg.el so we never touch handles in other slots.
      const stalesOnEl = arg.el.querySelectorAll<HTMLElement>(".time-range-resize-handle");
      if (stalesOnEl.length > 0) {
        const removed = new Set<HTMLElement>();
        for (const s of stalesOnEl) {
          removed.add(s);
          s.remove();
        }
        // Prune the Map of refs we just orphaned so it doesn't accumulate.
        for (const [k, els] of injectedHandlesRef.current) {
          const live = els.filter((el) => !removed.has(el));
          if (live.length === 0) injectedHandlesRef.current.delete(k);
          else if (live.length !== els.length) injectedHandlesRef.current.set(k, live);
        }
      }

      const make = (which: "top" | "bottom") => {
        const h = document.createElement("div");
        h.className = `time-range-resize-handle time-range-resize-handle-${which}`;
        h.setAttribute("role", "button");
        h.setAttribute(
          "aria-label",
          which === "top" ? "Resize selection start" : "Resize selection end",
        );
        h.addEventListener("pointerdown", (e) =>
          beginResizeRef.current(e, which, arg.el, id, start, end),
        );
        // Stop touchstart so FullCalendar's PointerDragging doesn't begin a
        // new selection from the handle press.
        h.addEventListener("touchstart", (e) => e.stopPropagation(), { passive: false });
        return h;
      };
      const top = make("top");
      const bot = make("bottom");
      arg.el.appendChild(top);
      arg.el.appendChild(bot);
      const existing = injectedHandlesRef.current.get(id) ?? [];
      existing.push(top, bot);
      injectedHandlesRef.current.set(id, existing);
    },
    [],
  );

  /**
   * Counterpart to `handleEventDidMount`. FC fires this right before
   * removing the event element from the DOM — closer in time to the
   * actual unmount than our additionalEvents-driven sweep, and it fires
   * per-instance so we don't have to guess which slot the unmount
   * affected. Removes only handles whose elements live inside arg.el.
   */
  const handleEventWillUnmount = useCallback(
    (arg: { event: { extendedProps: Record<string, unknown> }; el: HTMLElement }) => {
      if (!arg.event.extendedProps["isTimeRangeSelection"]) return;
      const handlesOnEl = arg.el.querySelectorAll<HTMLElement>(".time-range-resize-handle");
      if (handlesOnEl.length === 0) return;
      const removed = new Set<HTMLElement>();
      for (const h of handlesOnEl) {
        removed.add(h);
        h.remove();
      }
      for (const [k, els] of injectedHandlesRef.current) {
        const live = els.filter((el) => !removed.has(el));
        if (live.length === 0) injectedHandlesRef.current.delete(k);
        else if (live.length !== els.length) injectedHandlesRef.current.set(k, live);
      }
    },
    [],
  );
  const handleEventDidMountRef = useRef(handleEventDidMount);
  handleEventDidMountRef.current = handleEventDidMount;

  // Safety: if the component unmounts mid-drag, detach document listeners.
  // Reads handler refs from gesture state so we remove the exact identities
  // that beginResize registered.
  useEffect(() => {
    return () => {
      const state = resizeStateRef.current;
      if (state) {
        document.removeEventListener("pointermove", state.moveHandler);
        document.removeEventListener("pointerup", state.upHandler);
        document.removeEventListener("pointercancel", state.upHandler);
      }
    };
  }, []);

  // Orphan-handle GC. eventWillUnmount handles the common per-instance
  // case; this sweep covers ranges that disappear without FC firing
  // unmount (pooled bg-event elements). Walks our Map — populated in
  // eventDidMount — and drops anything whose id isn't in the current
  // additionalEvents.
  useLayoutEffect(() => {
    const validIds = new Set<string>();
    if (additionalEvents) {
      for (const e of additionalEvents) {
        if (e.id != null) validIds.add(String(e.id));
      }
    }
    for (const [id, els] of injectedHandlesRef.current) {
      if (!validIds.has(id)) {
        for (const el of els) el.remove();
        injectedHandlesRef.current.delete(id);
      }
    }
  }, [additionalEvents]);

  // Carousel mode: all mobile/tablet views use 3 side-by-side FullCalendar
  // instances so day/week/month share the same swipe-between-pages behavior.
  const isCarousel = !isWide;

  // Unit-aware date offset: day/multi-day → days, week → weeks, month → months.
  // Used by all carousel date computations so the swipe unit matches the current
  // view. Multi-day swipes one day at a time (slots overlap by one day) — same
  // as iOS Calendar's 2-day landscape view.
  const shiftByView = useCallback(
    (d: Date, offset: number) => {
      switch (viewType) {
        case "day":
        case "multi-day":
          return addDays(d, offset);
        case "week":
          return addWeeks(d, offset);
        case "month":
          return addMonths(d, offset);
      }
    },
    [viewType],
  );
  const shiftByViewRef = useRef(shiftByView);
  shiftByViewRef.current = shiftByView;

  // --- Slot dates for the carousel ---
  // slotDates[1] is always the visible center (current period).
  const [slotDates, setSlotDates] = useState<[Date, Date, Date]>(() => [
    subDays(currentDate, 1),
    currentDate,
    addDays(currentDate, 1),
  ]);

  // Keep a stable ref so touch handlers can read latest slotDates without stale closures
  const slotDatesRef = useRef(slotDates);
  slotDatesRef.current = slotDates;

  const onDateChangeRef = useRef(onDateChange);
  onDateChangeRef.current = onDateChange;

  // --- Event loading ---
  // For carousel: preload all 3 slot ranges individually per current viewType.
  // Multi-day view on mobile renders as a 2-day grid (timeGrid2Day) so each
  // slot must fetch two days of events. Single-day / week / month fetch one
  // unit each (week/month ignore `dayCount`).
  const slotDayCount = viewType === "multi-day" ? 2 : 1;
  const { events: slotEvents0 } = useCalendarEvents(
    slotDates[0],
    viewType,
    calendarIds,
    slotDayCount,
  );
  const { events: slotEvents1 } = useCalendarEvents(
    slotDates[1],
    viewType,
    calendarIds,
    slotDayCount,
  );
  const { events: slotEvents2 } = useCalendarEvents(
    slotDates[2],
    viewType,
    calendarIds,
    slotDayCount,
  );

  // For non-carousel views (week/month/desktop day)
  const { events: viewEvents } = useCalendarEvents(currentDate, viewType, calendarIds);

  // Filter hidden calendars
  const filterEvents = useCallback(
    (evts: typeof viewEvents) => {
      const visible =
        !hiddenCalendarIds || hiddenCalendarIds.size === 0
          ? evts
          : evts.filter((e) => !hiddenCalendarIds.has(e.extendedProps.calendarId));
      // Ring the focused event (see calendar.css).
      return stampHighlightedEvent(visible, highlightedInstanceKey);
    },
    [hiddenCalendarIds, highlightedInstanceKey],
  );

  const filteredSlotEvents0 = useMemo(() => filterEvents(slotEvents0), [filterEvents, slotEvents0]);
  const filteredSlotEvents1 = useMemo(() => filterEvents(slotEvents1), [filterEvents, slotEvents1]);
  const filteredSlotEvents2 = useMemo(() => filterEvents(slotEvents2), [filterEvents, slotEvents2]);
  const filteredViewEvents = useMemo(() => filterEvents(viewEvents), [filterEvents, viewEvents]);

  // Fetch calendar metadata for color assignment
  const { data: calendars } = useQuery<Calendar[]>({
    queryKey: ["calendars"],
    queryFn: getCalendars,
  });
  useRegisteredCalendarColors(calendars);

  // --- Refs ---
  const calRef0 = useRef<FullCalendar>(null);
  const calRef1 = useRef<FullCalendar>(null); // center / single-calendar fallback
  const calRef2 = useRef<FullCalendar>(null);
  const slotRef0 = useRef<HTMLDivElement>(null);
  const slotRef1 = useRef<HTMLDivElement>(null);
  const slotRef2 = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  // Separate container for non-carousel mode — avoids sharing any DOM state with the carousel.
  const calContainerRef = useRef<HTMLDivElement>(null);
  // Start as true so FullCalendar's initial mount datesSet (which fires with
  // the week/month start, not today) is blocked before our [currentDate] effect runs.
  const isNavigatingRef = useRef(true);
  const scrollTopRef = useRef(0); // preserved scroll position across swipes

  // Touch tracking
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchDirectionRef = useRef<"horizontal" | "vertical" | null>(null);
  const animTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const midAnimTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCarouselRef = useRef(isCarousel);
  isCarouselRef.current = isCarousel;
  // True from touchStart until the swipe animation fully commits (animTimeout completes)
  // or snaps back. Guards the [currentDate] effect from resetting the row transform
  // while an animation is still in flight.
  const isSwipingRef = useRef(false);

  useEffect(() => {
    return () => {
      if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
      if (midAnimTimeoutRef.current) clearTimeout(midAnimTimeoutRef.current);
    };
  }, []);

  // Pixel dimensions for the carousel (computed from DOM on mount/resize)
  const slotWidthRef = useRef(0);
  const cardGapRef = useRef(0);

  // --- View mapping (non-carousel) ---
  const viewMapping = useMemo(
    () => (isWide ? VIEW_TYPE_TO_FULLCALENDAR : VIEW_TYPE_TO_FULLCALENDAR_MOBILE),
    [isWide],
  );
  const currentFullCalendarView = viewMapping[viewType];
  const isListView = currentFullCalendarView.startsWith("list");
  const isTimeGrid = currentFullCalendarView.startsWith("timeGrid");

  // --- Carousel row ref (callback ref so position is set on every mount) ---
  const rowEl = useRef<HTMLDivElement | null>(null);

  /**
   * Measures the outer container and computes pixel dimensions for the carousel.
   * Returns the measured values and stores them in refs for use in touch handlers.
   */
  const measureCarousel = useCallback(() => {
    const el = outerRef.current;
    if (!el) return null;
    const slotWidth = el.clientWidth;
    const gap = Math.round(el.getBoundingClientRect().left * 2);
    // Only update refs when we have a real measurement — never let a zero
    // clientWidth (iOS Safari before layout settles) poison a valid value.
    if (slotWidth > 0) {
      slotWidthRef.current = slotWidth;
      cardGapRef.current = gap;
    }
    return { slotWidth, gap };
  }, []);

  /**
   * Applies pixel widths/gaps to the carousel row and slots, then centers the row.
   * Must be called after the row and slot DOM elements are mounted.
   *
   * If the fresh clientWidth measurement returns 0 (iOS Safari before layout settles),
   * falls back to the cached slotWidthRef so a recovery call (e.g. Today button) still
   * works even when the DOM hasn't fully painted yet.
   */
  const applyCarouselLayout = useCallback(() => {
    const row = rowEl.current;
    if (!row) return;
    const m = measureCarousel();
    // Prefer fresh measurement; fall back to cached ref so recovery paths (Today button,
    // post-swipe reconciliation) can always fix the layout even if clientWidth is 0.
    const slotWidth = m?.slotWidth || slotWidthRef.current;
    const gap = m?.slotWidth ? m.gap : cardGapRef.current;
    if (!slotWidth) return; // Nothing useful to apply — not yet measured
    row.style.width = `${3 * slotWidth + 2 * gap}px`;
    row.style.columnGap = `${gap}px`;
    for (const ref of [slotRef0, slotRef1, slotRef2]) {
      if (ref.current) ref.current.style.width = `${slotWidth}px`;
    }
    row.style.transform = `translateX(-${slotWidth + gap}px)`;
  }, [measureCarousel]);

  const rowRef = useCallback(
    (el: HTMLDivElement | null) => {
      rowEl.current = el;
      if (el) {
        // Attempt sync layout (often succeeds on desktop). The post-paint useEffect
        // below is the reliable fallback for iOS Safari where clientWidth may be 0
        // at React commit time. The old double-rAF approach was removed because it
        // could fire AFTER the post-paint useEffect and overwrite slotWidthRef with 0.
        applyCarouselLayout();
      }
    },
    [applyCarouselLayout],
  );

  // Carousel: watch outerRef for size changes and re-apply the pixel layout.
  useEffect(() => {
    if (!isCarousel) return;
    const el = outerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      applyCarouselLayout();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isCarousel, applyCarouselLayout]);

  // Reliable post-paint measurement. requestAnimationFrame fires *before* paint so
  // clientWidth can still be 0 on iOS Safari. useEffect fires *after* paint, when the
  // browser has completed layout and clientWidth is guaranteed to be correct.
  useEffect(() => {
    if (!isCarousel) return;
    applyCarouselLayout();
  }, [isCarousel, applyCarouselLayout]);

  // Non-carousel: watch calContainerRef and tell FullCalendar to re-measure on resize.
  useEffect(() => {
    if (isCarousel) return;
    const el = calContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      calRef1.current?.getApi().updateSize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isCarousel]);

  // Today button: scroll to current time and hard-reset the carousel.
  // Hard-resets cancel any in-flight animations, rebuild all three slots around today,
  // and re-apply the pixel layout — recovering any broken state without a page refresh.
  useEffect(() => {
    if (!scrollToNowKey) return;

    if (isCarousel) {
      // Cancel any in-flight swipe animation so we start from a clean slate.
      if (animTimeoutRef.current) {
        clearTimeout(animTimeoutRef.current);
        animTimeoutRef.current = null;
      }
      if (midAnimTimeoutRef.current) {
        clearTimeout(midAnimTimeoutRef.current);
        midAnimTimeoutRef.current = null;
      }
      isSwipingRef.current = false;

      // Rebuild all three slots around today and snap the row back to center.
      const today = new Date();
      const resetDates: [Date, Date, Date] = [shiftByView(today, -1), today, shiftByView(today, 1)];
      slotDatesRef.current = resetDates;
      setSlotDates(resetDates);
      isNavigatingRef.current = true;
      calRef0.current?.getApi().gotoDate(resetDates[0]);
      calRef1.current?.getApi().gotoDate(resetDates[1]);
      calRef2.current?.getApi().gotoDate(resetDates[2]);
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 0);

      // Re-apply pixel layout to fix any broken transform.
      // 1. Clear any active CSS transition so the fix is instantaneous, not animated
      //    (a leftover "transform 280ms ease" from a swipe would delay the snap).
      if (rowEl.current) rowEl.current.style.transition = "";
      // 2. Immediate attempt (may get clientWidth=0 on iOS Safari if paint hasn't fired).
      applyCarouselLayout();
      // 3. Post-paint retry — guaranteed to have correct clientWidth after the browser
      //    has completed layout. Mirrors the reliable post-paint pattern used on mount.
      requestAnimationFrame(() => applyCarouselLayout());
    }

    const now = new Date();
    const duration = { hour: Math.max(0, now.getHours() - 1), minute: now.getMinutes() };
    for (const ref of [calRef0, calRef1, calRef2]) {
      ref.current?.getApi().scrollToTime(duration);
    }
    // Keep scrollTopRef in sync so carousel swipes preserve the new position.
    requestAnimationFrame(() => {
      const s = getSlotScroller(slotRef1.current);
      if (s) scrollTopRef.current = (s as HTMLElement).scrollTop;
    });
  }, [scrollToNowKey, isCarousel, applyCarouselLayout, shiftByView]);

  // Scroll to an explicit time on mount, when `initialScrollTime` changes, or
  // when `scrollToTimeKey` bumps for the same time. Set from a calendar focus
  // link carrying a range, so the grid lands at the relevant time-of-day instead
  // of the default (current time).
  const lastScrollSignatureRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!initialScrollTime) return;
    // Both inputs are read here so a repeat request (same time, new key) scrolls
    // while an incidental re-render doesn't. Callers without a key — the
    // availability picker — key purely off the time, as before.
    const signature = `${initialScrollTime}#${scrollToTimeKey ?? ""}`;
    if (signature === lastScrollSignatureRef.current) return;
    lastScrollSignatureRef.current = signature;
    const [hStr, mStr] = initialScrollTime.split(":");
    const hour = Number(hStr);
    const minute = Number(mStr ?? "0");
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return;
    const duration = { hour: Math.max(0, hour), minute };
    for (const ref of [calRef0, calRef1, calRef2]) {
      ref.current?.getApi().scrollToTime(duration);
    }
  }, [initialScrollTime, scrollToTimeKey]);

  // --- Sync view / date when props change ---
  useEffect(() => {
    isNavigatingRef.current = true;
    const activeRefs = isCarousel ? [calRef0, calRef1, calRef2] : [calRef1];
    for (const ref of activeRefs) {
      ref.current?.getApi().changeView(currentFullCalendarView);
    }
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 0);
  }, [currentFullCalendarView, isCarousel]);

  useEffect(() => {
    // While a swipe animation is in flight, the animTimeout handlers own all
    // carousel state (slotDates, gotoDate, transform). Letting this effect run
    // concurrently corrupts the animation by loading wrong events into the slots
    // being revealed and flickering the center slot. The effect will fire again
    // after isSwipingRef is cleared (when animTimeout calls onDateChange, which
    // triggers a re-render with the new currentDate).
    if (isCarousel && isSwipingRef.current) return;

    isNavigatingRef.current = true;
    if (isCarousel) {
      const newDates: [Date, Date, Date] = [
        shiftByView(currentDate, -1),
        currentDate,
        shiftByView(currentDate, 1),
      ];
      slotDatesRef.current = newDates;
      setSlotDates(newDates);
      calRef0.current?.getApi().gotoDate(newDates[0]);
      calRef1.current?.getApi().gotoDate(newDates[1]);
      calRef2.current?.getApi().gotoDate(newDates[2]);
      if (rowEl.current) {
        rowEl.current.style.transform = `translateX(-${slotWidthRef.current + cardGapRef.current}px)`;
      }
    } else {
      calRef1.current?.getApi().gotoDate(currentDate);
    }
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 0);
  }, [currentDate, isCarousel, shiftByView]);

  // datesSet fires on internal navigation — only center calendar has this handler
  const handleDatesSet = useCallback(
    (dateInfo: DatesSetArg) => {
      if (isNavigatingRef.current) return;
      onDateChange(dateInfo.start);
    },
    [onDateChange],
  );

  // --- Non-passive touchmove: tracks finger and translates the carousel row ---
  useEffect(() => {
    if (!isCarousel) return;
    const el = outerRef.current;
    if (!el) return;
    const onMove = (e: TouchEvent) => {
      if (!isCarouselRef.current) return;
      // Resize handle drags own their own gesture and never become a swipe.
      // The plain `isSelectingRef` check used to live here, but it ate horizontal
      // pans whenever FC's long-press had armed — once the carousel commits to
      // horizontal we cancel FC's selection below instead.
      if (isResizingRef.current) return;
      const deltaX = e.touches[0].clientX - touchStartX.current;
      const deltaY = e.touches[0].clientY - touchStartY.current;
      // Movement detected — abort the pending tap-hold preview (the user is
      // dragging, not holding). If the timer already fired, also clear the
      // overlay so it doesn't linger during the drag.
      if (holdTimerRef.current !== null && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      if (!touchDirectionRef.current && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
        touchDirectionRef.current =
          Math.abs(deltaX) >= Math.abs(deltaY) ? "horizontal" : "vertical";
        // First time direction is decided: if horizontal, sync the offscreen
        // slots' scrollTop now (deferred from touchstart to avoid tripping
        // FullCalendar's `wasTouchScroll` flag — see handleTouchStart note).
        // Safe here because a selection can't start mid-horizontal-drag anyway.
        if (touchDirectionRef.current === "horizontal") {
          // Latch the swipe — `selectAllow` reads this and refuses to arm FC for
          // the rest of this gesture. Also tear down any selection that FC managed
          // to start before the carousel claimed the gesture (rare with a 500ms
          // long-press, but harmless if not).
          swipeWonRef.current = true;
          if (isSelectingRef.current) {
            isSelectingRef.current = false;
            clearDragOverlay();
            clearMonthHighlight();
            calRef0.current?.getApi().unselect();
            calRef1.current?.getApi().unselect();
            calRef2.current?.getApi().unselect();
          }
          for (const ref of [slotRef0, slotRef2]) {
            const s = getSlotScroller(ref.current);
            if (s && s.scrollTop !== scrollTopRef.current) {
              (s as HTMLElement).scrollTop = scrollTopRef.current;
            }
          }
        }
      }
      // Vertical scroll with an in-progress selection: let FC keep the gesture.
      if (touchDirectionRef.current === "vertical" && isSelectingRef.current) return;
      if (touchDirectionRef.current === "horizontal") {
        // Tap tolerance: real-finger taps drift a few px. Don't preventDefault
        // until on-axis delta is clearly a drag, otherwise the browser-
        // synthesized click on the underlying event button is suppressed
        // (W3C Touch Events §5) and tapping events on mobile stops working.
        if (Math.abs(deltaX) < 12) return;
        e.preventDefault();
        if (rowEl.current) {
          rowEl.current.style.transition = "";
          rowEl.current.style.transform = `translateX(${-(slotWidthRef.current + cardGapRef.current) + deltaX}px)`;
        }
      }
    };
    // touchcancel fires when iOS Safari intercepts the touch (e.g. system edge-swipe).
    // Without this, isSwipingRef stays true permanently and blocks the [currentDate] effect.
    const onCancel = () => {
      isSwipingRef.current = false;
      touchDirectionRef.current = null;
      // Snap row back to center in case it was mid-drag
      if (rowEl.current) {
        rowEl.current.style.transition = "transform 280ms ease";
        rowEl.current.style.transform = `translateX(-${slotWidthRef.current + cardGapRef.current}px)`;
      }
    };
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchcancel", onCancel);
    return () => {
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchcancel", onCancel);
    };
  }, [isCarousel, clearDragOverlay, clearMonthHighlight]);

  // Deps intentionally empty — all captured values are refs, not reactive state.
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchDirectionRef.current = null;
    isSwipingRef.current = true;
    // Fresh gesture — clear the latch so FC can select normally until/unless
    // the carousel claims this gesture as a swipe.
    swipeWonRef.current = false;
    if (animTimeoutRef.current) {
      clearTimeout(animTimeoutRef.current);
      animTimeoutRef.current = null;
    }
    if (midAnimTimeoutRef.current) {
      clearTimeout(midAnimTimeoutRef.current);
      midAnimTimeoutRef.current = null;
    }
    if (isCarouselRef.current) {
      // Capture scroll position of center slot before swipe begins
      const scroller = getSlotScroller(slotRef1.current);
      if (scroller) scrollTopRef.current = scroller.scrollTop;
      // NOTE: We intentionally do NOT sync slot 0/2 scrollTop here anymore.
      // Setting scrollTop programmatically fires a `scroll` event on each slot,
      // which FullCalendar's PointerDragging catches via its window-scope
      // capture-phase scroll listener (attached on touchstart). That sets
      // `wasTouchScroll=true`, and `DateSelecting.touchScrollAllowed=false`
      // then blocks the tap-hold-drag gesture from starting a selection
      // (observed bug: after any vertical scroll, tap-hold-drag stops working).
      // Instead, we defer slot 0/2 sync until the user commits to a horizontal
      // swipe (see touchmove handler) — at that point a selection is no longer
      // possible (finger is moving) so wasTouchScroll becoming true is harmless.
      // Reset row to center (cancels any in-progress animation)
      if (rowEl.current) {
        rowEl.current.style.transition = "";
        rowEl.current.style.transform = `translateX(-${slotWidthRef.current + cardGapRef.current}px)`;
      }
    }

    // Press-preview (touch) is now scheduled by `handlePressStart` via
    // `onPointerDown`, which covers mouse + touch + pen on both the carousel
    // and non-carousel (desktop / tablet-landscape) branches.
  }, []);

  /**
   * Unified press-preview scheduler. Runs on `pointerdown` for any pointer type:
   *   - mouse → 0ms delay (immediate pressed state on click, mirroring a button)
   *   - touch / pen → 150ms delay (matches FC's `selectLongPressDelay`, avoids
   *     flashing on quick taps)
   *
   * Cancels on >5px movement (user committed to a drag — `selectAllow` takes over)
   * or on `pointerup` (the existing safety-cleanup effect clears the overlay).
   *
   * Attached to BOTH the carousel root (phones / narrow tablets) and the
   * non-carousel root (desktop / tablet-landscape), so every user gets the
   * same pressed-state feedback on press regardless of viewport or pointer.
   */
  const handlePressStart = useCallback(
    (e: React.PointerEvent) => {
      if (!selectableRef.current) return;
      const target = e.target;
      // Pointerdowns that land on an event chip belong to the event-drag /
      // event-click gesture, not cell selection. Skipping here keeps the
      // tap-hold press-preview from flashing under the grabbed event.
      // `.fc-event-main` / `.fc-event-main-frame` are FC's inner wrappers
      // — `.closest('.fc-event')` covers them all.
      if (target instanceof Element && target.closest(".fc-event")) return;
      const startX = e.clientX;
      const startY = e.clientY;
      const delay = e.pointerType === "mouse" ? 0 : 150;
      // Reset the hold latch for this gesture; `dateClick` reads it to decide
      // whether a touch tap commits a selection (see the FC `dateClick` wrapper).
      holdPreviewFiredRef.current = false;
      pressPointerTypeRef.current = e.pointerType;

      if (holdTimerRef.current !== null) clearTimeout(holdTimerRef.current);
      holdTimerRef.current = window.setTimeout(() => {
        holdTimerRef.current = null;
        holdPreviewFiredRef.current = true;
        renderTapHoldPreviewRef.current(target, startX, startY);
      }, delay);

      // Document-level listeners so we catch movement even when the pointer
      // leaves the calendar element (e.g. mouse drags past the edge).
      const onMove = (ev: PointerEvent) => {
        if (Math.abs(ev.clientX - startX) > 5 || Math.abs(ev.clientY - startY) > 5) {
          if (holdTimerRef.current !== null) {
            clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
          }
          cleanup();
        }
      };
      const onUp = () => cleanup();
      const cleanup = () => {
        // Released (or cancelled) before the hold fired → cancel the pending
        // preview so a quick tap doesn't flash it post-release, and leaves
        // `holdPreviewFiredRef` false so `dateClick` treats it as a quick tap.
        if (holdTimerRef.current !== null) {
          clearTimeout(holdTimerRef.current);
          holdTimerRef.current = null;
        }
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.removeEventListener("pointercancel", onUp);
        // Safety-net clear: if the press didn't produce a state change (e.g. pointer
        // released on a regular event → `eventClick` bypasses both `dateClick` and
        // `select`, so `additionalEvents` doesn't change and the useLayoutEffect
        // below never re-fires), the preview would otherwise persist indefinitely.
        // The 150ms delay is long enough that a normal press → dateClick → commit
        // path has already cleared the preview via the useLayoutEffect — this
        // timeout then harmlessly no-ops. Short enough that the visual remnant is
        // imperceptible in the edge case.
        //
        // Skip if a FC selection is in progress: `selectAllow` fires continuously
        // during a drag and re-renders the drag overlay each time. Clearing here
        // mid-drag produces a one-frame blank until the next `selectAllow` tick —
        // visible as a flash at the start of every click-drag.
        window.setTimeout(() => {
          if (isSelectingRef.current) return;
          clearDragOverlay();
          clearMonthHighlight();
        }, 150);
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      document.addEventListener("pointercancel", onUp);
    },
    [clearDragOverlay, clearMonthHighlight],
  );

  // Deps intentionally empty — all captured values are refs, not reactive state.
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    // Long-press selection in progress — let FullCalendar handle it, skip carousel logic
    if (isSelectingRef.current) {
      isSwipingRef.current = false;
      isSelectingRef.current = false;
      return;
    }
    if (!isCarouselRef.current || touchDirectionRef.current !== "horizontal") {
      // Tap or vertical scroll — no swipe committed, clear the swiping flag so
      // the [currentDate] effect is not permanently suppressed.
      isSwipingRef.current = false;
      return;
    }
    const row = rowEl.current;
    if (!row) return;

    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const THRESHOLD = 50;
    const sw = slotWidthRef.current;
    const g = cardGapRef.current;
    const centerOffset = sw + g;

    if (Math.abs(deltaX) > THRESHOLD) {
      const goPrev = deltaX > 0; // swiped right = previous period
      const targetTransform = goPrev ? "translateX(0px)" : `translateX(-${2 * centerOffset}px)`;

      // Always derive new dates from the center slot (slotDates[1]), which is the only
      // slot guaranteed to be up-to-date during rapid swiping. If a previous swipe's
      // mid-animation timeout fired (updating slot 1) but its final timeout was
      // cancelled by this new touch, slots 0 and 2 are stale — computing from them
      // produces duplicate or skipped dates and leaves the carousel in a broken state.
      // shiftByViewRef reflects the current viewType (day / week / month) so the
      // swipe unit matches what the user is seeing.
      const shift = shiftByViewRef.current;
      const centerDate = slotDatesRef.current[1];
      const newCurrentDate = shift(centerDate, goPrev ? -1 : 1);
      const newDates: [Date, Date, Date] = [
        shift(newCurrentDate, -1),
        newCurrentDate,
        shift(newCurrentDate, 1),
      ];

      // Phase 1: animate row to show adjacent slot
      row.style.transition = "transform 280ms ease";
      row.style.transform = targetTransform;

      // Mid-animation: update slot 1 (center) while it is off-screen.
      // React and FullCalendar render the new events invisibly, so when
      // Phase 2 snaps the row back to center there is no event flash.
      midAnimTimeoutRef.current = setTimeout(() => {
        midAnimTimeoutRef.current = null;
        isNavigatingRef.current = true;
        calRef1.current?.getApi().gotoDate(newDates[1]);
        // Update slotDatesRef immediately so any rapid handleTouchEnd that fires
        // before the React render sees the correct center date.
        const midDates: [Date, Date, Date] = [
          slotDatesRef.current[0],
          newDates[1],
          slotDatesRef.current[2],
        ];
        slotDatesRef.current = midDates;
        setSlotDates(midDates);
        const s = getSlotScroller(slotRef1.current);
        if (s) (s as HTMLElement).scrollTop = scrollTopRef.current;
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 0);
      }, 100);

      animTimeoutRef.current = setTimeout(() => {
        animTimeoutRef.current = null;
        // Phase 2: instant reset — snap row back and update the two outer slots.
        // Slot 1 was already updated mid-animation so its events won't re-render.
        isNavigatingRef.current = true;
        row.style.transition = "";
        row.style.transform = `translateX(-${centerOffset}px)`;

        calRef0.current?.getApi().gotoDate(newDates[0]);
        calRef2.current?.getApi().gotoDate(newDates[2]);

        // Update slotDatesRef immediately so any touchStart that fires before the
        // React render reads the correct dates for the next swipe.
        slotDatesRef.current = newDates;
        setSlotDates(newDates);
        // Clear swiping flag before calling onDateChange so the [currentDate]
        // effect that fires from the resulting re-render can reset the transform
        // if no new swipe has started by then.
        isSwipingRef.current = false;
        onDateChangeRef.current(newCurrentDate);
        requestAnimationFrame(() => {
          setTimeout(() => {
            isNavigatingRef.current = false;
          }, 0);
        });
      }, 280);
    } else {
      // Snap back to center — no date change, so [currentDate] effect won't fire.
      // Clear swiping flag immediately.
      isSwipingRef.current = false;
      row.style.transition = "transform 280ms ease";
      row.style.transform = `translateX(-${centerOffset}px)`;
    }
  }, []);

  const renderEventContent = useCallback(
    (eventInfo: EventContentArg) => (
      <EventContent eventInfo={eventInfo} isListView={isListView} isTimeGrid={isTimeGrid} />
    ),
    [isListView, isTimeGrid],
  );

  // In timegrid views (week/day), convert allDay background events to timed events
  // so they render as full-column highlights instead of thin bars in the narrow all-day row.
  // dayGrid (month) needs allDay: true to render bg-events at all (splitAllDaySegsByRow filter).
  const adjustedAdditionalEvents = useMemo(() => {
    if (!additionalEvents) return undefined;
    if (!isTimeGrid) return additionalEvents;
    return additionalEvents.map((e) => (e.allDay ? { ...e, allDay: false } : e));
  }, [additionalEvents, isTimeGrid]);

  // Merge calendar events with additional events (e.g. selected availability ranges)
  const mergedViewEvents = useMemo(
    () =>
      adjustedAdditionalEvents
        ? [...filteredViewEvents, ...adjustedAdditionalEvents]
        : filteredViewEvents,
    [filteredViewEvents, adjustedAdditionalEvents],
  );
  const mergedSlotEvents0 = useMemo(
    () =>
      adjustedAdditionalEvents
        ? [...filteredSlotEvents0, ...adjustedAdditionalEvents]
        : filteredSlotEvents0,
    [filteredSlotEvents0, adjustedAdditionalEvents],
  );
  const mergedSlotEvents1 = useMemo(
    () =>
      adjustedAdditionalEvents
        ? [...filteredSlotEvents1, ...adjustedAdditionalEvents]
        : filteredSlotEvents1,
    [filteredSlotEvents1, adjustedAdditionalEvents],
  );
  const mergedSlotEvents2 = useMemo(
    () =>
      adjustedAdditionalEvents
        ? [...filteredSlotEvents2, ...adjustedAdditionalEvents]
        : filteredSlotEvents2,
    [filteredSlotEvents2, adjustedAdditionalEvents],
  );

  // Event drag also requires @fullcalendar/interaction (same plugin that
  // powers `selectable`). Either flag turns it on.
  const interactionEnabled = !!selectable || !!eventDragFcProps || !!onEventChange;
  const sharedCalendarProps = {
    plugins: interactionEnabled
      ? [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]
      : [dayGridPlugin, timeGridPlugin, listPlugin],
    headerToolbar: false as const,
    eventContent: renderEventContent,
    // Only when asked for: left unset, FullCalendar defaults it to the slot
    // duration, which is what every surface but the proposed-times picker wants.
    // Reaches the carousel too — `carouselCalendarProps` spreads this.
    ...(snapDuration && { snapDuration }),
    ...(selectable && {
      selectable: true,
      selectMirror: true,
      selectOverlap: true,
      // On touch devices, require a full platform-standard long-press before
      // starting selection. 500ms is iOS Safari's own long-press threshold and is
      // long enough that a touch-then-pan gesture (even one with a brief settle
      // before sliding) never arms FC selection. Selection is intentional only:
      // press and hold ≥500ms, then release for a single-slot range or drag for
      // a wider range. Belt-and-suspenders via `swipeWonRef` in `selectAllow`.
      selectLongPressDelay: 500,
      // Require 5px of movement before FC treats a gesture as a selection. Without
      // this, a long-press-no-drag release fires BOTH `select` (single-cell zero-
      // distance selection) and `dateClick` for the same gesture, and the two
      // toggles cancel out ("tap-hold flashes and reverts"). FC docs: "useful for
      // differentiating a selection from a dateClick". 5px is small enough that
      // genuine drags still register from the very first cell crossed.
      selectMinDistance: 5,
      dateClick: onDateClick
        ? (info: { date: Date; allDay: boolean }) => {
            // Touch: a single-cell selection requires a deliberate press-and-hold
            // (the tap-hold preview must have fired). A quick tap on an empty cell
            // does nothing — you tap-and-hold to select, or tap an event to open
            // it. Mouse clicks still select immediately.
            if (pressPointerTypeRef.current !== "mouse" && !holdPreviewFiredRef.current) return;
            onDateClick(info);
          }
        : undefined,
      selectAllow: (span: { start: Date; end: Date; allDay: boolean }) => {
        // Carousel claimed this gesture as a swipe — refuse FC for the rest of
        // the touch. Stays false until the next touchstart resets the latch.
        if (swipeWonRef.current) return false;
        const isFirstCall = !isSelectingRef.current;
        isSelectingRef.current = true;
        if (isFirstCall) onSelectStartRef.current?.(span);
        clearDragOverlay();
        clearMonthHighlight();
        if (span.allDay) {
          // Month view: rectangular highlight on individual day cells (columns × rows).
          // In carousel mode, scope the cell lookup to slot 1 (the visible center slot)
          // so we don't hit the prev/next months' duplicate cells for boundary weeks.
          highlightMonthCells(span.start, span.end, isCarousel ? slotRef1.current : null);
        } else {
          // Timegrid: pixel-positioned overlay box across day columns.
          // slotSpanMinutes must match the grid's actual height: carousel extends
          // past midnight by one slot (slotMaxTime 24:30:00 = 1470 min) for the
          // "12 AM" end-of-day label; non-carousel uses FC's default 24:00:00.
          const slotSpanMinutes = isCarousel ? 1470 : 1440;
          renderDragOverlay(computeDragBoxes(span.start, span.end, slotSpanMinutes));
        }
        return true; // Selection is always allowed — toggleTimeRanges handles add/subtract logic
      },
      select: (info: DateSelectArg) => {
        isSelectingRef.current = false;
        clearDragOverlay();
        clearMonthHighlight();
        onSelect?.(info);
        // Defer unselect to avoid FullCalendar's synchronous flushSync re-render
        // blocking the main thread while additionalEvents are being updated.
        requestAnimationFrame(() => info.view.calendar.unselect());
      },
    }),
    // Single source of truth for event clicks. FullCalendar's delegated click
    // listener on `.fc-event` catches clicks on both desktop and mobile; the
    // event content itself has no onClick. (Previously the button had its own
    // onClick, but with React 17+ root delegation, FC's native listener on the
    // `.fc` container fires first on bubble, so stopPropagation in the button
    // ran too late and both handlers fired — producing ghost history entries
    // on swap and with `pushHistory: true`.)
    eventClick: (info: {
      event: { id: string; extendedProps: Record<string, unknown> };
      el: HTMLElement;
      jsEvent: Event;
    }) => {
      info.jsEvent.preventDefault();
      // Pending-sync events are visually disabled via `pointer-events-none`
      // on the inner content div, but FC's delegated listener on .fc-event
      // still receives the click (since the anchor itself doesn't have
      // pointer-events disabled). Ignore the click to match the visual state.
      if (info.event.extendedProps["pendingSyncUpdate"]) return;
      // Selection-range and availability bg-events have their own handler.
      // Range bg-events sit on top with `pointer-events: auto`, so any tap on
      // a highlighted range fires this branch — whether or not a real event
      // chip sits beneath it. Routing decides what "tap on range" means
      // (typically: remove the range).
      if (
        onRawEventClick &&
        (info.event.extendedProps["isTimeRangeSelection"] ||
          info.event.extendedProps["isSelectedAvailability"])
      ) {
        onRawEventClick(info);
        return;
      }
      const sourceId = sourceIdOf(info.event);
      if (sourceId && onEventClick) {
        onEventClick(sourceId, info.el);
        return;
      }
      // Fallback: no onEventClick provided (e.g. CalendarPreview) — open the
      // Google Calendar URL in a new tab. Pickers that render real events only
      // as read-only context opt out via `disableEventLinks`.
      if (disableEventLinks) return;
      const htmlLink = info.event.extendedProps["htmlLink"];
      if (typeof htmlLink === "string" && htmlLink) {
        window.open(htmlLink, "_blank", "noopener,noreferrer");
      }
    },
    eventDidMount: handleEventDidMount,
    eventWillUnmount: handleEventWillUnmount,
    height: "100%" as const,
    expandRows: true,
    fixedWeekCount: false,
    nowIndicator: true,
    dayMaxEvents: true,
    moreLinkClick: "popover" as const,
    // Scroll to 1 hour before current time on initial render so the current hour is visible —
    // unless a focus link asked for a time: a grid mounted by that link would otherwise
    // apply this default after the scroll effect and bury the highlighted range.
    scrollTime:
      initialScrollTime ??
      `${String(Math.max(0, new Date().getHours() - 1)).padStart(2, "0")}:00:00`,
    // Preserve scroll position after gotoDate navigation instead of resetting to scrollTime.
    // This eliminates the scroll flash when the carousel rotates slots.
    scrollTimeReset: false,
    slotLabelContent: (arg: { date: Date }) => {
      const hour = arg.date.getHours();
      const meridiem = hour >= 12 ? "PM" : "AM";
      const h = hour % 12 || 12;
      return `${h} ${meridiem}`;
    },
    eventTimeFormat: {
      hour: "numeric" as const,
      minute: "2-digit" as const,
      meridiem: "short" as const,
      // "10am", not "10:00am". Most meetings start and end on the hour, so the
      // zeroes were the most-repeated characters on the grid while carrying no
      // information — and a chip has little enough width that the ones which do
      // start at :15 or :30 read faster once they are the only times with
      // minutes on them. Brings the tall chips in line with the short ones,
      // whose own `timeStr` has always dropped a zero minute.
      omitZeroMinute: true,
    },
    noEventsContent: "No events",
    // Show top-edge resize handle when an event is start-editable (off by
    // default so other consumers' editable events only get the bottom handle).
    eventResizableFromStart: eventResizableFromStart ?? false,
    eventResize: (info: {
      event: {
        id: string;
        start: Date | null;
        end: Date | null;
        extendedProps: Record<string, unknown>;
      };
      revert: () => void;
    }) => {
      if (!info.event.start || !info.event.end) {
        info.revert();
        return;
      }
      onEventResizeRef.current?.({
        event: { id: sourceIdOf(info.event), start: info.event.start, end: info.event.end },
        revert: info.revert,
      });
    },
    // Touch long-press to activate event drag. Differentiates from a quick tap
    // (which opens the event) and matches the long-press already governing cell
    // selection (`selectLongPressDelay: 150`). Tuned slightly longer so a
    // selection-intent long-press on whitespace doesn't accidentally pick up an
    // overlapping event chip. No effect on mouse drags (which start instantly).
    eventLongPressDelay: 250,
    // Drag-to-reschedule. Spread last so these override any defaults above
    // (notably `editable: true`). No-op when `eventDragFcProps` is undefined.
    // Note: when drag wiring is active it sets `eventDurationEditable: false`,
    // which suppresses the resize handles above — drag-to-reschedule and
    // drag-to-resize are mutually exclusive on a given calendar instance for
    // now (no caller wants both, and the two gestures would compete for the
    // same pointerdown).
    ...(eventDragFcProps ?? {}),
    // Lightweight drag/resize for the proposed-slots editor. Only fires when an
    // event carries `editable: true` (FullCalendar gates the interaction on that
    // flag even with the plugin + handler present). Spread last so it wins over
    // the resize wiring above for callers that opt in via `onEventChange`; bare
    // callers (which leave `onEventChange` unset) are unaffected.
    ...(onEventChange && {
      eventDrop: (info: {
        event: { id: string; start: Date | null; end: Date | null };
        revert: () => void;
      }) => onEventChange({ event: info.event, revert: info.revert }),
      eventResize: (info: {
        event: { id: string; start: Date | null; end: Date | null };
        revert: () => void;
      }) => onEventChange({ event: info.event, revert: info.revert }),
    }),
  };

  // Carousel instances use a fixed (non-expanding) row layout so the CSS slot
  // height is honored instead of being stretched to fill the mobile viewport
  // — matches the desktop day-sidebar treatment.
  // slotMaxTime extends past midnight by one slot so FullCalendar's native
  // label machinery renders a "12 AM" end-of-day label (via slotLabelContent
  // above) just like the other hourly labels — the top border of that slot
  // doubles as the 12 AM divider line. Paired with CSS scroll-area padding
  // so the 12 AM row can be scrolled above the fixed Omnibox (BIG-4095).
  const carouselCalendarProps = {
    ...sharedCalendarProps,
    expandRows: false,
    slotMaxTime: "24:30:00",
  };

  // Carousel and non-carousel use completely separate root elements so there is no shared
  // DOM state (scrollLeft, overflow context) that could contaminate the other mode.
  if (isCarousel) {
    return (
      <div
        key="carousel"
        ref={outerRef}
        className={cn(
          "flex-1 min-h-0 w-full overflow-hidden",
          disableEventLinks && "calendar-events-readonly",
        )}
        onPointerDown={handlePressStart}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Three-slot carousel: [prev | current | next]
            Widths and gap are set in px by applyCarouselLayout after mount.
            Events populate once loaded; FullCalendar renders immediately to avoid a blank screen. */}
        <div ref={rowRef} className="flex h-full">
          <div
            ref={slotRef0}
            className="h-full shrink-0 overflow-hidden calendar-compact-sidebar border-t border-border"
            data-view={viewType}
          >
            <div className="h-full fc-wrapper">
              <FullCalendar
                ref={calRef0}
                initialView={currentFullCalendarView}
                initialDate={slotDates[0]}
                events={mergedSlotEvents0}
                views={CAROUSEL_VIEW_CONFIG}
                {...carouselCalendarProps}
              />
              <CalendarFadeScrollbar />
            </div>
          </div>
          <div
            ref={slotRef1}
            className="h-full shrink-0 overflow-hidden calendar-compact-sidebar border-t border-border"
            data-view={viewType}
          >
            <div className="h-full fc-wrapper">
              <FullCalendar
                ref={calRef1}
                initialView={currentFullCalendarView}
                initialDate={slotDates[1]}
                events={mergedSlotEvents1}
                views={CAROUSEL_VIEW_CONFIG}
                datesSet={handleDatesSet}
                {...carouselCalendarProps}
              />
              <CalendarFadeScrollbar />
            </div>
          </div>
          <div
            ref={slotRef2}
            className="h-full shrink-0 overflow-hidden calendar-compact-sidebar border-t border-border"
            data-view={viewType}
          >
            <div className="h-full fc-wrapper">
              <FullCalendar
                ref={calRef2}
                initialView={currentFullCalendarView}
                initialDate={slotDates[2]}
                events={mergedSlotEvents2}
                views={CAROUSEL_VIEW_CONFIG}
                {...carouselCalendarProps}
              />
              <CalendarFadeScrollbar />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Non-carousel branch is desktop-only: `isCarousel = !isWide`, so we only
  // reach this return when `isWide` is true.
  return (
    <div
      key="non-carousel"
      ref={calContainerRef}
      className={cn("flex-1 min-h-0 w-full", disableEventLinks && "calendar-events-readonly")}
      onPointerDown={handlePressStart}
    >
      {/* Single calendar for week/month/desktop.
          Renders immediately; events populate once loaded to avoid a blank screen. */}
      <div className="h-full fc-wrapper w-full">
        <FullCalendar
          ref={calRef1}
          initialView={currentFullCalendarView}
          initialDate={currentDate}
          events={mergedViewEvents}
          datesSet={handleDatesSet}
          views={{
            timeGridWeek: {
              dayHeaderContent: renderWeekDayHeader,
              slotDuration: slotDurationProp ?? "00:30:00",
            },
            timeGridDay: {
              dayHeaderContent: renderWeekDayHeader,
              slotDuration: slotDurationProp ?? "00:30:00",
            },
          }}
          {...sharedCalendarProps}
        />
        <CalendarFadeScrollbar />
      </div>
    </div>
  );
};
